import { useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { colors } from "@rubi/ui";
import { useTranslation } from "react-i18next";

interface ContactNameEditorProps {
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber: string;
  onSave: (firstName: string, lastName: string) => Promise<void>;
  formatPhoneNumber: (phone: string) => string;
}

const nameInputStyles = {
  width: 120,
  "& .MuiInput-root": {
    backgroundColor: "action.hover",
    borderRadius: "4px",
    padding: "8px 12px",
    "&:before": {
      borderBottom: "1px solid var(--divider)",
    },
    "&:after": {
      borderBottom: "1px solid var(--divider)",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottom: "1px solid var(--divider)",
    },
  },
  "& .MuiInput-input": {
    color: "text.primary",
    fontSize: "14px",
    "&::placeholder": {
      color: "text.disabled",
      opacity: 1,
    },
  },
};

const ContactNameEditor = ({
  firstName,
  lastName,
  phoneNumber,
  onSave,
  formatPhoneNumber,
}: ContactNameEditorProps) => {
  const { t } = useTranslation("message");
  const [isEditing, setIsEditing] = useState(false);
  const [tempFirstName, setTempFirstName] = useState("");
  const [tempLastName, setTempLastName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = () => {
    setTempFirstName(firstName || "");
    setTempLastName(lastName || "");
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onSave(tempFirstName, tempLastName);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving name:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const handleCancel = () => {
    setTempFirstName("");
    setTempLastName("");
    setIsEditing(false);
  };

  const displayName =
    firstName || lastName
      ? `${firstName || ""} ${lastName || ""}`.trim()
      : formatPhoneNumber(phoneNumber);

  const hasName = firstName || lastName;

  if (isEditing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          size="small"
          placeholder={t("contact.firstName")}
          value={tempFirstName}
          onChange={(e) => setTempFirstName(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={nameInputStyles}
          variant="standard"
        />
        <TextField
          size="small"
          placeholder={t("contact.lastName")}
          value={tempLastName}
          onChange={(e) => setTempLastName(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={nameInputStyles}
          variant="standard"
        />
        <IconButton
          size="small"
          onClick={handleSave}
          disabled={isSaving}
          sx={{ color: colors.success.light }}
        >
          <CheckIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleCancel}
          sx={{ color: colors.legacy.coral }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {displayName}
        </Typography>
        {hasName && (
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, color: "text.secondary" }}
          >
            {formatPhoneNumber(phoneNumber)}
          </Typography>
        )}
        {!hasName && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              fontSize: "0.875rem",
            }}
          >
            {t("contact.addName")}
          </Typography>
        )}
      </Box>
      <Tooltip title={t("contact.editContactName")}>
        <IconButton
          size="small"
          onClick={handleEdit}
          sx={{
            color: "text.secondary",
            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ContactNameEditor;
